import { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Card, CircularProgress, Chip, IconButton, Tooltip,
  List, ListItemButton, ListItemText, ListItemIcon, Divider, TextField,
  InputAdornment, Badge, Button, Stack, LinearProgress,
} from '@mui/material'
import {
  Fullscreen, FullscreenExit, Search, FiberManualRecord,
  Speed, Navigation, MyLocation, Close, Favorite, Height, Place,
  Sensors, GpsFixed, GpsNotFixed, OpenInNew, Memory, Person,
  Pin, Phone, Schedule,
} from '@mui/icons-material'
import { MapContainer, Popup, Polygon, Circle, CircleMarker, useMap } from 'react-leaflet'
import MapTileLayer from '../../components/MapTileLayer'
import { SmoothMarker } from '../../components/SmoothMarker'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { useTwinDevices, TwinDevice } from '../../hooks/useDigitalTwin'
import { useDevices } from '../../hooks/useDevices'
import { useGeofences } from '../../hooks/useFleet'
import { createVehicleIcon } from '../../lib/vehicleIcon'
import { fmtRelative } from '../../lib/dateFormat'
import { statusColor, statusLabel, statusSortOrder } from '../../lib/deviceStatus'
import '../../lib/vehicleIcon.css'
import { MapZonesLayer } from './MapV2Layers'
import { MapV2Toolbar, type FleetMode } from './MapV2Toolbar'

// ── Types ────────────────────────────────────────────────────────

interface MapDevice {
  id: string
  name: string
  device_identifier: string
  status: string
  vehicle_type?: string | null
  plate_number?: string | null
  owner_name?: string | null
  device_type?: string | null
  phone_number?: string | null
  timezone?: string | null
  last_latitude?: number | null
  last_longitude?: number | null
  last_speed?: number | null
  last_heading?: number | null
  last_altitude?: number | null
  last_seen_at?: string | null
  health_score?: number
  sensor_data?: Record<string, unknown>
  updated_at?: string | null
}

function unwrapDevices(d: unknown): { id: string; name: string; device_identifier: string; vehicle_type?: string; plate_number?: string; owner_name?: string; device_type?: string; phone_number?: string; timezone?: string; last_seen_at?: string | null }[] {
  if (Array.isArray(d)) return d
  const o = d as { devices?: any[] } | undefined
  return o?.devices ?? []
}

// ── Map controller: follow the selected vehicle ─────────────────

function FollowVehicle({ deviceId, position, enabled, zoom }: { deviceId: string | null; position: [number, number] | null; enabled: boolean; zoom?: number }) {
  const map = useMap()
  const followedId = useRef<string | null>(null)
  useEffect(() => {
    if (!enabled || !deviceId || !position) {
      followedId.current = null
      return
    }
    if (followedId.current !== deviceId) {
      followedId.current = deviceId
      map.flyTo(position, zoom ?? 16, { duration: 1.2 })
      return
    }
    map.panTo(position, { animate: true, duration: 1 })
  }, [deviceId, position?.[0], position?.[1], enabled, zoom, map])
  return null
}

// Fit all devices into view on first load
function FitBounds({ devices, requestId }: { devices: MapDevice[]; requestId: number }) {
  const map = useMap()
  const fitted = useRef(false)
  const lastRequestId = useRef(requestId)
  useEffect(() => {
    const requested = lastRequestId.current !== requestId
    if ((fitted.current && !requested) || devices.length === 0) return
    const pts = devices
      .filter((d) => d.last_latitude != null && d.last_longitude != null)
      .map((d) => [d.last_latitude!, d.last_longitude!] as [number, number])
    if (pts.length === 0) return
    fitted.current = true
    lastRequestId.current = requestId
    if (pts.length === 1) {
      map.flyTo(pts[0], 15, { duration: 1 })
    } else {
      map.flyToBounds(L.latLngBounds(pts), { padding: [50, 50], duration: 1, maxZoom: 15 })
    }
  }, [devices, requestId, map])
  return null
}

function formatTelemetryValue(value: unknown): string {
  if (value == null) return '--'
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: 2, bgcolor: '#f1f5f9', color: '#64748b', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="#94a3b8" sx={{ display: 'block', lineHeight: 1.2 }}>{label}</Typography>
        <Typography variant="body2" fontWeight={650} color="#1e293b" sx={{ overflowWrap: 'anywhere' }}>{value}</Typography>
      </Box>
    </Box>
  )
}

function VehicleDetailsPanel({
  device,
  following,
  onToggleFollow,
  onClose,
  onViewDetails,
}: {
  device: MapDevice
  following: boolean
  onToggleFollow: () => void
  onClose: () => void
  onViewDetails: () => void
}) {
  const { t } = useTranslation()
  const color = statusColor(device.status)
  const sensors = Object.entries(device.sensor_data ?? {}).sort(([a], [b]) => a.localeCompare(b))
  const health = Math.max(0, Math.min(100, device.health_score ?? 0))

  const openDirections = () => {
    if (device.last_latitude == null || device.last_longitude == null) return
    window.open(`https://www.google.com/maps/search/?api=1&query=${device.last_latitude},${device.last_longitude}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <Card sx={{
      position: 'absolute', top: 64, right: 12, bottom: 76, zIndex: 1000,
      width: { xs: 'calc(100% - 24px)', sm: 350 }, maxHeight: 590,
      border: '1px solid rgba(203,213,225,.9)', borderRadius: 3,
      boxShadow: '0 18px 45px rgba(15,23,42,.2)', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', bgcolor: 'rgba(255,255,255,.97)',
      backdropFilter: 'blur(12px)',
    }}>
      <Box sx={{ p: 2, background: 'linear-gradient(135deg,#081b2e 0%,#0d3858 100%)', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: color + '22', border: `1px solid ${color}66` }}>
            <Navigation sx={{ color, transform: `rotate(${device.last_heading ?? 0}deg)` }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800} fontSize={17} noWrap>{device.name}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.58)', display: 'block' }} noWrap>
              {device.plate_number || device.device_identifier || t('map.unknownDevice')}
            </Typography>
            <Chip
              icon={<FiberManualRecord sx={{ fontSize: '9px !important', color: `${color} !important` }} />}
              label={t(`map.${device.status}`, statusLabel(device.status))} size="small"
              sx={{ mt: 1, height: 23, bgcolor: color + '22', color: '#fff', fontWeight: 700, border: `1px solid ${color}55` }}
            />
          </Box>
          <IconButton size="small" onClick={onClose} aria-label={t('common.close')} sx={{ color: 'rgba(255,255,255,.72)' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ overflowY: 'auto', p: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, mb: 2 }}>
          {[
            { label: t('map.speed'), value: `${(device.last_speed ?? 0).toFixed(0)}`, unit: 'km/h', icon: <Speed /> },
            { label: t('map.heading'), value: `${(device.last_heading ?? 0).toFixed(0)}°`, unit: '', icon: <Navigation /> },
            { label: t('twin.health'), value: `${health}`, unit: '/100', icon: <Favorite /> },
          ].map((metric) => (
            <Box key={metric.label} sx={{ p: 1.1, borderRadius: 2.25, bgcolor: '#f8fafc', border: '1px solid #e8eef5', textAlign: 'center' }}>
              <Box sx={{ color: '#34A8E0', '& svg': { fontSize: 17 } }}>{metric.icon}</Box>
              <Typography fontWeight={800} fontSize={15} color="#0f172a" sx={{ lineHeight: 1.15 }}>
                {metric.value}<Typography component="span" fontSize={9} color="#94a3b8"> {metric.unit}</Typography>
              </Typography>
              <Typography fontSize={9.5} color="#94a3b8">{metric.label}</Typography>
            </Box>
          ))}
        </Box>

        <Stack spacing={1.35}>
          {device.last_latitude != null && device.last_longitude != null && (
            <DetailRow icon={<Place sx={{ fontSize: 17 }} />} label={t('map.location')} value={`${device.last_latitude.toFixed(5)}, ${device.last_longitude.toFixed(5)}`} />
          )}
          {device.last_altitude != null && <DetailRow icon={<Height sx={{ fontSize: 17 }} />} label={t('map.altitude')} value={`${device.last_altitude.toFixed(0)} m`} />}
          {device.owner_name && <DetailRow icon={<Person sx={{ fontSize: 17 }} />} label={t('map.owner')} value={device.owner_name} />}
          {device.vehicle_type && <DetailRow icon={<Pin sx={{ fontSize: 17 }} />} label={t('map.vehicleType')} value={device.vehicle_type.replace(/_/g, ' ')} />}
          {device.device_type && <DetailRow icon={<Memory sx={{ fontSize: 17 }} />} label={t('map.deviceType')} value={device.device_type.replace(/_/g, ' ')} />}
          {device.phone_number && <DetailRow icon={<Phone sx={{ fontSize: 17 }} />} label={t('map.phone')} value={device.phone_number} />}
          {device.timezone && <DetailRow icon={<Schedule sx={{ fontSize: 17 }} />} label={t('map.timezone')} value={device.timezone} />}
          <DetailRow icon={<Schedule sx={{ fontSize: 17 }} />} label={t('map.lastSeen')} value={device.last_seen_at ? fmtRelative(device.last_seen_at) : '--'} />
          {device.updated_at && <DetailRow icon={<Schedule sx={{ fontSize: 17 }} />} label={t('map.updated')} value={fmtRelative(device.updated_at)} />}
        </Stack>

        <Box sx={{ mt: 2, p: 1.25, borderRadius: 2.25, bgcolor: '#f8fafc', border: '1px solid #e8eef5' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .75 }}>
            <Typography variant="caption" fontWeight={700} color="#475569">{t('map.deviceHealth')}</Typography>
            <Typography variant="caption" fontWeight={800} color={health >= 70 ? '#16a34a' : health >= 40 ? '#d97706' : '#dc2626'}>{health}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={health} sx={{ height: 6, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: health >= 70 ? '#22c55e' : health >= 40 ? '#f59e0b' : '#ef4444', borderRadius: 4 } }} />
        </Box>

        {sensors.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} color="#334155" sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: 1 }}>
              <Sensors sx={{ fontSize: 17, color: '#34A8E0' }} /> {t('map.sensorData')} <Chip label={sensors.length} size="small" sx={{ height: 19, fontSize: 9 }} />
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: .75 }}>
              {sensors.map(([key, value]) => (
                <Box key={key} sx={{ p: 1, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e8eef5', minWidth: 0 }}>
                  <Typography fontSize={9.5} color="#94a3b8" noWrap>{key.replace(/_/g, ' ')}</Typography>
                  <Typography variant="body2" fontWeight={700} color="#334155" noWrap title={formatTelemetryValue(value)}>{formatTelemetryValue(value)}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 'auto', p: 1.5, borderTop: '1px solid #e8eef5', bgcolor: '#fff' }}>
        <Stack direction="row" spacing={1}>
          <Button fullWidth size="small" variant={following ? 'contained' : 'outlined'} startIcon={following ? <GpsFixed /> : <GpsNotFixed />} onClick={onToggleFollow}>
            {following ? t('map.following') : t('map.follow')}
          </Button>
          <Tooltip title={t('map.openMaps')}><IconButton onClick={openDirections} sx={{ border: '1px solid #dbe5ef', borderRadius: 2 }}><OpenInNew fontSize="small" /></IconButton></Tooltip>
          <Tooltip title={t('map.viewDetails')}><IconButton onClick={onViewDetails} sx={{ border: '1px solid #dbe5ef', borderRadius: 2 }}><Memory fontSize="small" /></IconButton></Tooltip>
        </Stack>
      </Box>
    </Card>
  )
}

// ── Main component ───────────────────────────────────────────────

export default function MapPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: devicesRaw, isLoading: dLoading } = useDevices()
  const { data: twinDevices = [], isLoading: tLoading } = useTwinDevices()
  const { data: geofences = [], isLoading: gLoading } = useGeofences()

  const [isFullscreen, setIsFullscreen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [following, setFollowing] = useState(false)
  const [fitRequestId, setFitRequestId] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  // Wave 1 E1.3 — Live Map 2.0 controls
  const [fleetMode, setFleetMode] = useState<FleetMode>('fleet')
  const [zonesEnabled, setZonesEnabled] = useState(true)
  const [windowMin, setWindowMin] = useState(0)

  // Merge device names + live location
  const devices = useMemo<MapDevice[]>(() => {
    const deviceMeta = unwrapDevices(devicesRaw)
    const nameMap = new Map(deviceMeta.map((d) => [d.id, d]))
    return twinDevices
      .filter((t: TwinDevice) => t.last_latitude != null && t.last_longitude != null)
      .map((t: TwinDevice) => {
        const meta = nameMap.get(t.device_id)
        return {
          id: t.device_id,
          name: meta?.name ?? t.device_id.slice(0, 8),
          device_identifier: meta?.device_identifier ?? '',
          status: t.status,
          vehicle_type: meta?.vehicle_type ?? null,
          plate_number: meta?.plate_number ?? null,
          owner_name: meta?.owner_name ?? null,
          device_type: meta?.device_type ?? null,
          phone_number: meta?.phone_number ?? null,
          timezone: meta?.timezone ?? null,
          last_latitude: t.last_latitude,
          last_longitude: t.last_longitude,
          last_speed: t.last_speed,
          last_heading: t.last_heading,
          last_altitude: t.last_altitude,
          last_seen_at: t.last_seen_at ?? meta?.last_seen_at ?? null,
          health_score: t.health_score,
          sensor_data: t.sensor_data,
          updated_at: t.updated_at,
        }
      })
  }, [devicesRaw, twinDevices])

  // Sort: online first, then by name
  const sortedDevices = useMemo(() =>
    [...devices].sort((a, b) => {
      const ao = statusSortOrder(a.status)
      const bo = statusSortOrder(b.status)
      if (ao !== bo) return ao - bo
      return a.name.localeCompare(b.name)
    }),
    [devices]
  )

  const filteredDevices = useMemo(() => {
    let result = sortedDevices
    // Time scrubber: only show devices seen within the last `windowMin` minutes.
    // 0 disables the filter ("live" — every device).
    if (windowMin > 0) {
      const cutoff = Date.now() - windowMin * 60_000
      result = result.filter((d) => {
        if (!d.last_seen_at) return false
        return new Date(d.last_seen_at).getTime() >= cutoff
      })
    }
    if (!search) return result
    const q = search.toLowerCase()
    return result.filter((d) =>
      d.name.toLowerCase().includes(q) || d.device_identifier.toLowerCase().includes(q)
    )
  }, [sortedDevices, search, windowMin])

  const selectedDevice = useMemo(
    () => devices.find((d) => d.id === selectedId) ?? null,
    [devices, selectedId],
  )
  const followTarget = useMemo<[number, number] | null>(() => {
    if (selectedDevice?.last_latitude == null || selectedDevice.last_longitude == null) return null
    return [selectedDevice.last_latitude, selectedDevice.last_longitude]
  }, [selectedDevice?.last_latitude, selectedDevice?.last_longitude])

  const defaultCenter = useMemo<[number, number]>(() => {
    const withLoc = devices.find((d) => d.last_latitude != null && d.last_longitude != null)
    if (withLoc) return [withLoc.last_latitude!, withLoc.last_longitude!]
    return [33.3152, 44.3661]
  }, [devices])

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {})
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {})
    }
  }, [])

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  // Click a device in the sidebar
  const handleDeviceClick = useCallback((d: MapDevice) => {
    if (d.last_latitude != null && d.last_longitude != null) {
      setSelectedId(d.id)
      setFollowing(true)
      analytics.track(EVENTS.MAP_DEVICE_SELECTED, { device_id: d.id, device_name: d.name, status: d.status })
    }
  }, [])

  if (dLoading || tLoading || gLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  // statusColor and statusLabel imported from lib/deviceStatus

  return (
    <Box
      ref={containerRef}
      sx={{
        height: isFullscreen ? '100vh' : 'calc(100vh - 140px)',
        display: 'flex', flexDirection: 'column',
        bgcolor: isFullscreen ? '#fff' : 'transparent',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isFullscreen ? 1 : 2, px: isFullscreen ? 2 : 0, pt: isFullscreen ? 1 : 0 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
            {t('map.title')}
          </Typography>
          <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
            <Badge color="success" variant="dot" sx={{ mr: 1.5, '& .MuiBadge-dot': { width: 8, height: 8 } }}>
              <span />
            </Badge>
            {t('map.trackedDevices', { count: devices.length })} · {t('map.geofenceCount', { count: geofences.length })}
          </Typography>
        </Box>
        <Tooltip title={isFullscreen ? t('map.exitFullscreen') : t('map.fullscreen')}>
          <IconButton onClick={toggleFullscreen} sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main: Sidebar + Map */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 }, minHeight: 0, px: isFullscreen ? 2 : 0, pb: isFullscreen ? 2 : 0 }}>
        {/* Vehicle sidebar */}
        <Card sx={{ width: { xs: '100%', md: 280, lg: 300 }, height: { xs: 190, md: 'auto' }, flexShrink: 0, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Search */}
          <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0' }}>
            <TextField
              size="small" fullWidth placeholder={t('map.searchVehicles')}
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}
            />
          </Box>

          {/* Device list */}
          <List dense disablePadding sx={{ flex: 1, overflow: 'auto' }}>
            {filteredDevices.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="#94a3b8">{t('map.noVehicles')}</Typography>
              </Box>
            )}
            {filteredDevices.map((d, i) => (
              <Box key={d.id}>
                {i > 0 && <Divider />}
                <ListItemButton
                  selected={selectedId === d.id}
                  onClick={() => handleDeviceClick(d)}
                  sx={{
                    py: 1, px: 1.5,
                    '&.Mui-selected': { bgcolor: '#EBF6FD', borderLeft: '3px solid #34A8E0' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <FiberManualRecord sx={{ fontSize: 10, color: statusColor(d.status) }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600} color="#1e293b" noWrap>
                        {d.name}
                      </Typography>
                    }
                    secondary={
                      <Box component="span">
                        <Typography variant="caption" color="#94a3b8" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                          {d.last_speed != null && d.last_speed > 0 ? (
                            <>
                              <Speed sx={{ fontSize: 11 }} />
                              {d.last_speed.toFixed(0)} km/h
                              {d.status === 'towing' && <span style={{ color: '#ef4444', fontWeight: 700 }}> TOWING</span>}
                            </>
                          ) : (
                            <span style={{ color: statusColor(d.status), fontWeight: 600 }}>
                              {t(`map.${d.status}`, statusLabel(d.status))}
                            </span>
                          )}
                          {d.last_heading != null && d.last_speed != null && d.last_speed > 0 && (
                            <Navigation sx={{ fontSize: 11, transform: `rotate(${d.last_heading}deg)` }} />
                          )}
                        </Typography>
                        {d.last_seen_at && (
                          <Typography variant="caption" color="#cbd5e1" component="span" sx={{ display: 'block', fontSize: 10 }}>
                            {fmtRelative(d.last_seen_at)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {d.vehicle_type && (
                    <Chip label={d.vehicle_type} size="small" sx={{ fontSize: 10, height: 20, bgcolor: '#f1f5f9', color: '#64748b' }} />
                  )}
                </ListItemButton>
              </Box>
            ))}
          </List>

          {/* Footer stats */}
          <Box sx={{ p: 1, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            {(['online', 'idle', 'parked', 'towing', 'offline'] as const).map((s) => {
              const cnt = devices.filter((d) => d.status === s).length
              if (cnt === 0) return null
              return (
                <Chip key={s}
                  icon={<FiberManualRecord sx={{ fontSize: '8px !important', color: `${statusColor(s)} !important` }} />}
                  label={`${cnt} ${t(`map.${s}`, s)}`} size="small"
                  sx={{ fontSize: 10, bgcolor: statusColor(s) + '12', color: statusColor(s), fontWeight: 600 }} />
              )
            })}
          </Box>
        </Card>

        {/* Map */}
        <Card sx={{ flex: 1, minHeight: 320, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0', position: 'relative' }}>
          <MapContainer center={defaultCenter} zoom={11} style={{ height: '100%', width: '100%' }}>
            <MapTileLayer />

            <FitBounds devices={devices} requestId={fitRequestId} />
            <FollowVehicle deviceId={selectedId} position={followTarget} enabled={following} />

            {selectedDevice?.last_latitude != null && selectedDevice.last_longitude != null && (
              <CircleMarker center={[selectedDevice.last_latitude, selectedDevice.last_longitude]} radius={25}
                pathOptions={{ color: statusColor(selectedDevice.status), weight: 2, fillColor: statusColor(selectedDevice.status), fillOpacity: .08, dashArray: '4 4' }} />
            )}

            {/* Device markers */}
            {filteredDevices.map((d) =>
              d.last_latitude != null && d.last_longitude != null ? (
                <SmoothMarker
                  key={d.id}
                  position={[d.last_latitude!, d.last_longitude!]}
                  icon={createVehicleIcon(
                    fleetMode === 'asset' ? null : d.vehicle_type,
                    d.last_heading,
                    (d.last_speed ?? 0) > 0,
                  )}
                  duration={2000}
                  eventHandlers={{ click: () => handleDeviceClick(d) }}
                >
                  <Popup>
                    <Box sx={{ minWidth: 170 }}>
                      <Typography variant="subtitle2" fontWeight={800}>{d.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{t(`map.${d.status}`, statusLabel(d.status))} · {(d.last_speed ?? 0).toFixed(0)} km/h</Typography>
                    </Box>
                  </Popup>
                </SmoothMarker>
              ) : null,
            )}

            {/* Geofences */}
            {geofences.map((g) => {
              const geo = g.geometry as { coords?: number[][]; center?: number[]; radius_m?: number }
              if (g.shape === 'polygon' && geo?.coords?.length) {
                return (
                  <Polygon key={g.id} positions={geo.coords as [number, number][]}
                    pathOptions={{ color: g.color || '#34A8E0', weight: 2, fillOpacity: 0.15 }}>
                    <Popup>{g.name}</Popup>
                  </Polygon>
                )
              }
              if (g.shape === 'circle' && geo?.center && geo?.radius_m) {
                return (
                  <Circle key={g.id} center={geo.center as [number, number]} radius={geo.radius_m}
                    pathOptions={{ color: g.color || '#34A8E0', weight: 2, fillOpacity: 0.15 }}>
                    <Popup>{g.name}</Popup>
                  </Circle>
                )
              }
              return null
            })}

            {/* Wave 1 E1.3 — Custom Zones overlay (opt-in via toolbar) */}
            <MapZonesLayer enabled={zonesEnabled} />
          </MapContainer>

          {/* Wave 1 E1.3 — Live Map 2.0 controls (fleet/asset mode, zones, time scrubber) */}
          <MapV2Toolbar
            fleetMode={fleetMode}
            onFleetModeChange={setFleetMode}
            zonesEnabled={zonesEnabled}
            onZonesEnabledChange={setZonesEnabled}
            windowMin={windowMin}
            onWindowMinChange={setWindowMin}
          />

          {selectedDevice && (
            <VehicleDetailsPanel
              device={selectedDevice}
              following={following}
              onToggleFollow={() => setFollowing((value) => !value)}
              onClose={() => { setFollowing(false); setSelectedId(null) }}
              onViewDetails={() => navigate(`/devices/${selectedDevice.id}`)}
            />
          )}

          {/* Fit all button */}
          <Tooltip title={t('map.fitAllDevices')}>
            <IconButton
              onClick={() => { setFollowing(false); setFitRequestId((id) => id + 1) }}
              sx={{
                position: 'absolute', top: 12, right: 12, zIndex: 1000,
                bgcolor: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <MyLocation sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Card>
      </Box>
    </Box>
  )
}
